<template>
  <div class="MapLocationSelection">
    <b-aspect aspect="16:10">
      <MapBox
        class="RealisationDetail__map"
        :access-token="accessToken"
        :map-style.sync="mapStyle"
        :options="mapOptions"
        @load="onMapLoaded"
      >
        <LayerChargingpoints 
          :code="geographyCode" 
          :focus="coordinates !== null" />
        <LayerRequests
          :realisation-uuid="record.uuid"
          @click="handleOpenRequestPopup"
        />
        <LayerRealisationProcesses
          :key="`RealisationProcesses-${componentKey}`"
          :active-uuid="record.uuid"
          @click="handleOpenRealisationPopup"
        />

        <LayerLocationPicker
          :use-geocoder="true"
          :coordinates="coordinates"
          :locked="locked"
          @location="handleLocationPicker"
        />

        <PopupRequest
          :record="record"
          :uuid="requestUuid"
          @close="handleOpenRequestPopup"
        />

        <PopupRealisationProcess
          :key="`PopupRealisation-${componentKey}`"
          :uuid="popupRealisationUuid"
          @close="handleCloseRealisationProcessPopup"
        />

        <PopupChargingLocation />
      </MapBox>
    </b-aspect>
    <div class="AddressWrapper d-flex mt-3 w-100">
      <div class="AddressWrapper__instruction mt-3">
        <h4>Pin &amp; adres invoer</h4>
        <p>
          De plaatsing van de pin en het invoeren van het adres zijn geheel gescheiden.
        </p>
        <p>
          Bij het verschuiven van de pin verschijnt wel een adres suggestie welke naar wens overgenomen kan worden in de adres invoer.
        </p>
      </div>
      <div class="AddressWrapper__data">
        <div
          v-if="geocoderAddress"
          class="d-flex flex-column mt-3 w-100"
        >
          <div class="w-100 d-flex justify-content-between align-items-center">
            <strong>Pin - Adres suggestie ter overname</strong>
            <b-button
              v-if="revGeocoderComponents"
              class="ml-2 flex-shrink-0"
              size="sm"
              @click="handleUseGeocoderAddress"
            >
              Overnemen
              <b-icon icon="chevron-down" />
            </b-button>
          </div>
          <div class="d-flex flex-grow-1 mb-2">
            <div class="flex-grow-1">
              {{ geocoderAddress }}
            </div>
          </div>
          <b-button 
            v-if="isLinkedLocation"
            variant="outline-secondary"
            size="sm"
            @click="resetLocation"
          >
            Laatst vastgezette locatie
          </b-button>
        </div>
        <div class="d-flex flex-column mt-3 w-100">
          <div class="mr-2">
            <strong>Pin - Coordinaten</strong>
          </div>
        </div>
        <Address
          :values="addressValues"
          :disabled="locked || disabled"
          @address="handleAddressUpdate"
          @coordinates="handleAddressCoordinates"
        />
      </div>
    </div>
  </div>
</template>

<script>

import MapBox from '@/components/common/MapBox'
import LayerChargingpoints from '@/components/map/LayerChargingpoints'
import LayerRequests from '@/components/map/LayerRequests'
import LayerLocationPicker from '@/components/map/LayerLocationPicker'
import PopupRequest from '@/components/map/PopupRequest'
import { mapOptions, municipalityOptions } from '@/config'

import LayerRealisationProcesses from '@/components/map/LayerRealisationProcesses'
import PopupRealisationProcess from '@/components/map/PopupRealisationProcess'
import PopupChargingLocation from '@/components/map/PopupChargingLocation.vue'
import { getCoordinatesFromCityName } from '@/services/geocoder'

import { formatPostalCode } from '@/helpers/string'

import Address from '@/components/definitions/Address'
import { mapGetters } from 'vuex'

import { TextControl, LegendControl } from '@/services/mapbox'

import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder'
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css'

export default {
  name: 'MapDisconnectedLocationSelection',
  components: {
    MapBox,
    LayerRequests, LayerChargingpoints, LayerLocationPicker, PopupRequest,
    Address,
    LayerRealisationProcesses, PopupRealisationProcess, PopupChargingLocation,
  },
  props: {
    record: {
      type: Object,
      required: true,
    },
    values: {
      type: Object,
      required: true,
    },
    locked: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    municipalityCode: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      /**
       * MapBox instance
       */
      map: null,

      /**
       * MapBox SDK reference
       */
      mapbox: null,

      /**
       * MapBox is ready
       */
      loaded: false,

      /**
       * MapBox config
       */
      accessToken: process.env.VUE_APP_MAPBOX_TOKEN,
      mapStyle: process.env.VUE_APP_MAPBOX_STYLE,

      /**
       * MapBox geocoder field instance
       */
      geocoder: null,

      /**
       * The Google Reverse Geocoder output (based on dropped pin / coordinate input fields)
       */
      revGeocoderComponents: null,
      revGeocoderCoordinates: null,

      /**
       * The Google Geocoder output (based on address input fields)
       */

      /**
       * The input values of the Address fields
       */
      addressValues: null,

      /**
       * The UUID of the request pin that was clicked on.
       *  Passed on to the Request Popup
       */
      requestUuid: null,

      /**
       * The CTRL instruction
       */
      instructionMapControl: null,

      /**
       * realisation uuid of the realisation popup
       */
      popupRealisationUuid: null,
      componentKey: 0,
      center: []
    }
  },
  computed: {
    ...mapGetters('realisation', ['records']),
    /**
     * The connected requests
     *  // list of uuid's
     */
    requests() {
      return []
    },

    mapOptions() {
      if (this.requests.length) {
        return {
          // bbox: [
          //   4.728758231000029,
          //   52.27817457500004,
          //   5.068595450000032,
          //   52.43106396500008
          // ]
        }
      } else {
        let center = mapOptions.center || [4.9041, 52.3676]
        let focus = false

        if (this.coordinates) {
          focus = true
          center = [this.coordinates.lng, this.coordinates.lat]
        } else if (this.$route.query.lat && this.$route.query.lng) {
          focus = true
          center = [this.$route.query.lng, this.$route.query.lat]
        }

        return {
          center,
          zoom: focus ? 14 : (mapOptions.zoom || 11),
        }
      }
    },

    /**
     * Get the Geography code, use to show Charging Point Locations
     */
    geographyCode() {
      return this.record?.getGeographyCode() || null
    },

    /**
     *
     */
    coordinates() {
      if (this.revGeocoderCoordinates) {
        return {
          lng: this.revGeocoderCoordinates[0],
          lat: this.revGeocoderCoordinates[1],
        }
      }
      else if (this.addressValues && this.addressValues.Location && this.addressValues.Location.Latitude && this.addressValues.Location.Longitude) {
        return {
          lng: parseFloat(this.addressValues.Location.Longitude),
          lat: parseFloat(this.addressValues.Location.Latitude),
        }
      } else {
        return null
      }
    },

    isVlaanderen() {
      return process.env.VUE_APP_TENANT === 'vlaanderen'
    },

    /**
     *
     */
    geocoderAddress() {
      if (this.revGeocoderComponents === false) return 'De pin locatie leverde geen adres op.'

      if (! this.revGeocoderComponents) return ''
      let address = this.revGeocoderComponents

      return `${address.street}${address.street_number ? ' '+address.street_number : ''}, ${formatPostalCode(address.postalCode)} ${address.city}`
    },

    isLinkedLocation () {
      return this.municipalityCode && this.values.Municipality && (this.municipalityCode !== this.values.Municipality)
    }
  },
  watch: {
    record() {
      // Reset geocoder stuff
      this.revGeocoderComponents = null
      this.revGeocoderCoordinates = null
    },
    /**
     * Be on the look out for address changes triggered from the parent component
     */
    values() {
      this.addressValues = this.values
      this.revGeocoderCoordinates = null
      this.revGeocoderComponents = null
    },
    /**
     * Refresh Map and chargingpoints layer to show all locations if popup link to RP is used
     */
    '$route': {
      handler () {
        this.componentKey++
      },
    },
    locked() {
      if (this.instructionMapControl == null || ! this.map) return
        try {
        if (this.loaded && this.map.hasControl(this.instructionMapControl)) {
          this.map.removeControl(
            this.instructionMapControl,
          )
        } else {
          this.map.addControl(
            this.instructionMapControl,
          )
        }
      } catch (e) {
        //
      }
    },
    municipalityCode(code) {
      if (code !== this.values.Municipality) {
        this.setMapCenterAndLoadChargingPoints()
      }
    }
  },
  created() {
    this.addressValues = this.values
  },
  methods: {
    onMapLoaded({ map, mapbox }) {
      this.loaded = true
      this.map = map
      this.mapbox = mapbox

      // Add the GeoCoder plugin
      // This geocoder is only for navigation purposes
      // License restrictions prevent other usage
      this.geocoder = new MapboxGeocoder({
        accessToken: this.accessToken,
        mapboxgl: this.mapbox,

        countries: mapOptions.countries || 'nl',
        types: 'address',
        language: 'nl',

        collapsed: true,
        placeholder: 'Zoek op adres',
        marker: false,

        // Amsterdam
        // bbox: [
        //   4.728758231000029,
        //   52.27817457500004,
        //   5.068595450000032,
        //   52.43106396500008
        // ]
      })

      // this.geocoder.on('result', this.onGeocoderResult)
      this.map.addControl(
        this.geocoder,
      )

      this.instructionMapControl = new TextControl({ label: 'Klik en houd CTRL vast om de pin te (ver)plaatsen.' })
      if (! this.locked) {
        this.map.addControl(
          this.instructionMapControl,
        )
      }

      let legend = new LegendControl({
        items: [{
          label: 'Bestaande laadpaal',
          image: 'chargingpoint-realized',
        }, {
          label: 'Onbeoordeelde aanvraag',
          image: 'request-open',
        }, {
          label: 'Geaccepteerde aanvraag',
          image: 'request-accepted',
        }, {
          label: 'Gekoppeld aan ander proces',
          image: 'request-connected',
        }, {
          label: 'Gekoppeld aan dit proces',
          image: 'request-selected',
        }, {
          label: 'Filter op:',
        }, {
          label: 'Afgekeurde locatie',
          image: 'disabled',
          status: 'chargingpoint-rejected',
          filter: 'cancelled',
        }, {
          label: 'Toon aanvragen met afgerond RP',
          toggle: true,
          filter: 'completed',
        }],
      })
      this.map.addControl(
        legend,
      )
    },

    /**
     * When the user places the location pin
     */
    handleLocationPicker({ coordinates, components }) {
      this.revGeocoderComponents = components
      this.revGeocoderCoordinates = coordinates

      // Immediately update the coordinate fields
      this.addressValues.Location.Longitude = coordinates[0]
      this.addressValues.Location.Latitude = coordinates[1]
    },

    /**
     * When the user changes the coordinates in the Latitude / Longitude input fields
     */
    async handleAddressCoordinates({ lng, lat }) {
      this.flyToLocation({ center: [lng, lat] })

      this.revGeocoderCoordinates = [lng, lat]
    },

    /**
     * Handle a user initiated change in one of the address fields
     */
    handleAddressUpdate: async function({
      StreetName,
      HouseNumber,
      HouseNumberSuffix,
      PostalCode,
      CityName,
    }) {
      // Check if anything has changed
      if (
        this.addressValues.Location.StreetName !== StreetName ||
        this.addressValues.Location.HouseNumber !== HouseNumber ||
        this.addressValues.Location.HouseNumberSuffix !== HouseNumberSuffix ||
        this.addressValues.Location.PostalCode !== PostalCode ||
        this.addressValues.Location.CityName !== CityName
      ) {
        // Update the address
        this.addressValues.Location.StreetName = StreetName || ''
        this.addressValues.Location.HouseNumber = HouseNumber || ''
        this.addressValues.Location.HouseNumberSuffix = HouseNumberSuffix || ''
        this.addressValues.Location.PostalCode = formatPostalCode(PostalCode || '')
        this.addressValues.Location.CityName = CityName || ''
      }
    },

    /**
     *
     */
    handleOpenRequestPopup({ uuid }) {
      this.requestUuid = uuid
    },

    /**
     * https://stackoverflow.com/a/59368102
     */
    splitNumber(str) {
      const [, ...arr] = str.match(/(\d*)([\s\S]*)/)
      return arr
    },
    async handleUseGeocoderAddress() {
      let address = this.revGeocoderComponents

      let numberAndSuffix = this.splitNumber(address.street_number || '')

      this.handleAddressUpdate({
        StreetName: address.street,
        HouseNumber: numberAndSuffix[0] || '',
        HouseNumberSuffix: (numberAndSuffix[1] || '').trim(),
        PostalCode: formatPostalCode(address.postalCode),
        CityName: address.city,
      })

      let code = await this.handleCoordinateChange({
        lon: this.revGeocoderCoordinates[0],
        lat: this.revGeocoderCoordinates[1],
        placename: address.city,
      })

      if (code) {
        this.$emit('setCoordinates', { code })

        this.flyToLocation({ center: this.coordinates })
      }
    },

    resetLocation () {
      this.$emit('setCoordinates', { code: this.values.Municipality })
      this.flyToLocation({ center: this.coordinates })
    },

    handleOpenRealisationPopup({ uuid }) {
      this.popupRealisationUuid = uuid
    },
    handleCloseRealisationProcessPopup() {
      this.popupRealisationUuid = null
    },

    /**
     * Obtain
     */
    async handleCoordinateChange({ lat, lon, placename }) {
      if (this.isVlaanderen) {
        return municipalityOptions.find(o => o.text === placename)?.value
      } else {
        let api = await fetch(
          `https://api.pdok.nl/bzk/locatieserver/search/v3_1/free?q=${encodeURIComponent(placename)}&lat=${lat}&lon=${lon}&fq=type:adres&rows=1`,
        )

        if (! api.ok) {
          return
        }
        let response = await api.json()
        response = response.response

        if (response.numfound === 0) {
          return
        }

        return response.docs[0].gemeentecode || false
      }

    },
    async setMapCenterAndLoadChargingPoints() {
      const name = municipalityOptions.find(o => o.value === this.municipalityCode)?.text
      if (name) {
        this.center = await getCoordinatesFromCityName(name)
        this.flyToLocation({ center: this.center })
      }
    },

    flyToLocation ({ center }) {
      this.map.flyTo({
        center,
        duration: 2000,
        zoom: 14,
        essential: true,
      })
    }
  },
}
</script>

<style lang="scss">
.AddressWrapper {
  &__instruction {
    width: 30%;
    padding: 1rem;
    background: rgba(0, 0, 0, 0.05); // lighten($info, 40%);
  }
  &__data {
    margin-left: 2rem;
    width: calc(70% - 2rem)
  }
}
</style>
